
import { Component, Vue, Ref } from 'vue-property-decorator'
import { AiService } from '@/services/index'
import { UserModule } from '@/store/modules/user'
import { IAiProvider, IAiProviderType, IUpsertProviderParams } from '@/types/ai-types'
import EditDialog from './editDialog.vue'

enum Role {
    Admin = 1,
    Super = 2
}

@Component({
    name: 'AiProviderList',
    components: {
        EditDialog
    }
})
export default class extends Vue {
    @Ref('editDialogRef') private editDialog!: EditDialog

    private providers: IAiProvider[] = []
    private allProviders: IAiProvider[] = []
    private providerTypes: IAiProviderType[] = []
    /** 能力标签缓存：type -> { key: label }，按 type 懒加载，避免列表 N+1 请求 */
    private capabilityLabels: Record<string, Record<string, string>> = {}
    private capabilityLabelsLoaded: Set<string> = new Set()

    private pageIndex = 1
    private pageSize = 20
    private pageTotal = 0
    private loading = true
    private searchInput = ''
    private curType = ''
    private curStatus: number | '' = ''

    // 类型 code -> tag 类型（仅做视觉区分，未知类型走默认）
    private typeTagMap: any = {
        deepseek: '',
        openai: 'success',
        qwen: 'warning',
        custom: 'info'
    }

    /** 可见：管理员(1) 及以上 */
    private get canManage() {
        return UserModule.role >= Role.Admin
    }
    /** 写类操作（新建/编辑/删除）：仅超级管理员(2) */
    private get canWrite() {
        return UserModule.role === Role.Super
    }

    private mounted() {
        this.getProviderTypes()
        this.getProviders()
    }

    private async getProviderTypes() {
        const result = await AiService.listProviderTypes()
        if (result.status === 200) {
            this.providerTypes = result.data || []
        }
    }

    private async getProviders() {
        const params = {
            keyword: this.searchInput || undefined,
            type: this.curType || undefined,
            status: this.curStatus === '' ? undefined : this.curStatus,
            page: this.pageIndex,
            size: this.pageSize
        }
        this.loading = true
        const result = await AiService.listProviders(params)
        this.loading = false
        if (result.status === 200) {
            const data = result.data || {}
            this.allProviders = data.list || []
            this.pageTotal = data.page_total || data.total || 0
            this.applyLocalFilter()
            // 列表加载完成后，按当前页出现的 type 懒加载能力标签映射
            this.loadCapabilityLabelsForList()
        }
    }

    /** 按当前列表中出现的 type 并行拉取可用能力，构建 key→label 缓存 */
    private async loadCapabilityLabelsForList() {
        const types = [...new Set(this.allProviders.map(p => p.type).filter(Boolean))]
            .filter(t => !this.capabilityLabelsLoaded.has(t))
        if (types.length === 0) return
        await Promise.all(types.map(async (type) => {
            try {
                const res = await AiService.getAvailableCapabilities(type)
                if (res.status === 200 && res.data) {
                    const map: Record<string, string> = {}
                    res.data.forEach((c: any) => { map[c.key] = c.label })
                    this.$set(this.capabilityLabels, type, map)
                }
            } catch {
                // 拉取失败不阻塞列表，按 key 原样显示即可
            } finally {
                this.capabilityLabelsLoaded.add(type)
            }
        }))
    }

    /** 解析 capabilities JSON，返回所有 supported:true 的 key 数组 */
    private getSupportedCaps(row: any): string[] {
        try {
            if (!row.capabilities) return []
            const caps = typeof row.capabilities === 'string'
                ? JSON.parse(row.capabilities)
                : row.capabilities
            if (!caps || typeof caps !== 'object') return []
            return Object.keys(caps).filter(k => caps[k] && caps[k].supported)
        } catch {
            return []
        }
    }

    /** key→标签：优先取缓存的中文 label，没有则回退到 key */
    private getCapLabel(type: string, key: string): string {
        return (this.capabilityLabels[type] && this.capabilityLabels[type][key]) || key
    }

    /** 本地二次筛选：后端未支持 type/status 过滤时，用前端兜底 */
    private applyLocalFilter() {
        let list = [...this.allProviders]
        if (this.curType) {
            list = list.filter(i => i.type === this.curType)
        }
        if (this.curStatus !== '') {
            list = list.filter(i => i.status === this.curStatus)
        }
        if (this.searchInput) {
            const kw = this.searchInput.toLowerCase()
            list = list.filter(i =>
                i.name.toLowerCase().includes(kw) ||
                (i.type && i.type.toLowerCase().includes(kw))
            )
        }
        this.providers = list
    }

    private typeLabel(code: string) {
        const t = this.providerTypes.find(i => i.code === code)
        return t ? t.label : code
    }

    private typeTagType(code: string) {
        return this.typeTagMap[code] !== undefined ? this.typeTagMap[code] : ''
    }

    private onSearch() {
        this.resetPage()
        this.getProviders()
    }

    private onFilterChange() {
        this.resetPage()
        this.getProviders()
    }

    private onCurrentChange(page: number) {
        this.pageIndex = page
        this.getProviders()
    }

    private resetPage() {
        this.pageIndex = 1
    }

    /* ----------------------- 写类操作（仅超级管理员） ----------------------- */
    private onCreate() {
        this.editDialog.show('create')
    }

    private onEdit(row: IAiProvider) {
        this.editDialog.show('edit', row)
    }

    private async onSubmitCreate(params: IUpsertProviderParams) {
        const result = await AiService.createProvider(params)
        if (result.status === 200) {
            this.$message.success('创建成功')
            this.editDialog.hide()
            this.resetPage()
            this.getProviders()
        } else {
            this.$message.error(result.message || '创建失败')
        }
    }

    private async onSubmitUpdate(params: IUpsertProviderParams) {
        const result = await AiService.updateProvider(params)
        if (result.status === 200) {
            this.$message.success('修改成功')
            this.editDialog.hide()
            this.getProviders()
        } else {
            this.$message.error(result.message || '修改失败')
        }
    }

    private async onDelete(row: IAiProvider) {
        try {
            await this.$confirm(
                `确定删除「${row.name}」吗？已被业务功能绑定的模型不可删除。`,
                '提示',
                { type: 'warning' }
            )
        } catch {
            return
        }
        const result = await AiService.deleteProvider(row.id)
        if (result.status === 200) {
            this.$message.success('删除成功')
            this.getProviders()
        } else {
            this.$message.error(result.message || '删除失败')
        }
    }

    /* ----------------------- 启停 / 设为默认（管理员及以上） ----------------------- */
    private async onToggleStatus(row: IAiProvider, status: number) {
        const result = await AiService.setProviderStatus(row.id, status)
        if (result.status === 200) {
            this.$message.success(status === 1 ? '已启用' : '已禁用')
            row.status = status
        } else {
            this.$message.error(result.message || '操作失败')
        }
    }

    private async onSetDefault(row: IAiProvider) {
        const result = await AiService.setProviderDefault(row.id)
        if (result.status === 200) {
            this.$message.success('已设为默认')
            this.getProviders()
        } else {
            this.$message.error(result.message || '操作失败')
        }
    }
}
